import { ICurrentUser } from '@dadohr/shared/domainTypes/currentUser';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AddressField,
  DateField,
  DateFieldValidationParameters,
  EmailField,
  EnrollmentFormField,
  EnrollmentFormStatus,
  EnrollmentFormSubmissionDTO,
  TextField as EnrollmentTextField,
  FieldDataDestinationType,
  FIELDS_WHICH_CAN_RESTRICT_TO_EXISTING_VALUES,
  FieldType,
  FormFieldSubmission,
  getFieldKey,
  getUserFieldTarget,
  IEnrollmentForm,
  NumberField,
  SubmissionResponseStatus,
  SubmitFormResponse,
  TextRestriction,
  UserRelationField
} from '@dadohr/shared/domainTypes/enrollmentForms';
import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import {
  CustomUserField,
  DADO_TRY_OUT_ORG_ID,
  IOrganization
} from '@dadohr/shared/domainTypes/org';
import {
  CompositeUserFieldKey,
  DefaultDadoField,
  IUser,
  StandardUserField
} from '@dadohr/shared/domainTypes/user';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import {
  Button,
  ButtonBase,
  Dialog,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { KeyboardDatePicker } from '@material-ui/pickers';
import arrayMove from 'array-move';
import { compact, filter, flatten, sortBy } from 'lodash';
import { Code, GripVertical, Power, PowerOff, Trash2 } from 'lucide-react';
import moment from 'moment-timezone';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import shortid from 'shortid';
import { EnrollmentForm } from '../../../../../01-standalone-app/features/Experience/pages/EnrollmentForms';
import { StandaloneAppContextProvider } from '../../../../../01-standalone-app/hooks/useStandaloneAppContext';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { DialogActionsHeader } from '../../../../../components/DialogActionsHeader';
import { DialogActionsWithSlots } from '../../../../../components/DialogActionsWithSlots';
import { DialogContentWithoutPadding } from '../../../../../components/DialogContentWithoutPadding';
import { DiscardChangesDialog } from '../../../../../components/DiscardChangesDialog';
import { FormRow } from '../../../../../components/Form/FormRow';
import { LinkExternal } from '../../../../../components/LinkExternal';
import { MarkdownEditor } from '../../../../../components/MarkdownText';
import { RadioPanel } from '../../../../../components/RadioPanel';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useHover } from '../../../../../hooks/useHover';
import { usePromise } from '../../../../../hooks/usePromise';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import { useQueryParam } from '../../../../../routes';
import { useCurrentUser } from '../../../../../services/currentUser';
import {
  toEnrollmentFormDenormalized,
  toStandaloneAppEnrollmentFormUrl,
  useEnrollmentFormByTemplateOrGetEmptyDefault
} from '../../../../../services/experiences/enrollmentForms';
import { setDoc } from '../../../../../services/firebase';
import { lightMdToJsx, LightMdToJsx } from '../../../../../services/formatter';
import { roundNumberToNext } from '../../../../../services/numbers';
import { fromMoment, Timestamp, toMoment } from '../../../../../services/time';
import { createEmbeddableIframeForUrl } from '../../../../../services/url';
import {
  compositeUserFields,
  toSearchTermRegexp,
  useUserFieldLableMap,
  useUserFields
} from '../../../../../services/user';
import { DadoFieldOption } from '../../../../Users/pages/Imports/components/CustomImportMappingForm';
import { OptionBuilder } from '../../../components/Builders/TaskBuilder/components/BlockSettings/SubTask/Question';
import { EnrollmentFormSubmissionsPendingBubble } from './EnrollmentFormSubmissionsPendingBubble';
import { Submissions } from './Submissions';

const EnrollmentFormBuilderContainer = styled('div')((p) => ({
  background: p.theme.custom.colors.gray.w100,
  height: '100%',
  //width: '50vw',
  flexGrow: 1
}));

const LeftSideContainer = styled('div')((p) => ({
  flex: 1,
  overflow: 'auto',
  border: p.theme.custom.border.standard
}));

const RightSideContainer = styled('div')((p) => ({
  width: '37vw',
  overflow: 'auto'
}));

const FormContainer = styled('div')((p) => ({
  background: p.theme.custom.colors.gray.white,
  height: '100%'
}));

const FormStatus = styled('div')<{ status: EnrollmentFormStatus }>((p) => ({
  padding: `${p.theme.spacing(2)}px ${p.theme.spacing(3)}px`,
  backgroundColor:
    p.status === 'ACTIVE'
      ? p.theme.custom.colors['green'].w100
      : p.theme.custom.colors['gray'].w100,
  borderBottom: p.theme.custom.border.standard
}));

const FormBody = styled('div')((p) => ({
  padding: `${p.theme.spacing(2)}px ${p.theme.spacing(3)}px`,
  background: p.theme.custom.colors.gray.white
}));

const FormHeader = styled('div')((p) => ({
  padding: `${p.theme.spacing(1)}px ${p.theme.spacing(3)}px`,
  borderBottom: p.theme.custom.border.standard
}));

const FormSection = styled('div')((p) => ({
  padding: `${p.theme.spacing(2)}px ${p.theme.spacing(3)}px`,
  borderBottom: p.theme.custom.border.standard
}));

const FieldsContainer = styled('div')((p) => ({
  paddingRight: p.theme.spacing(4),
  borderRight: p.theme.custom.border.standard,
  flex: 2
}));

const ParametersContainer = styled('div')(() => ({
  flex: 3
}));

const DragDropContainer = styled('div')<{ highlight: boolean }>((p) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  background: p.highlight ? p.theme.custom.colors.green.w100 : 'transparent'
  // height: 'calc(100% + 30px)'
}));

// TODO might remove this and get BlockList from TaskBuilder to avoid duplicated code
const FieldsListItem = styled('div')<{
  dragging: boolean;
  disabled?: boolean;
  selected: boolean;
}>((p) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
  padding: `${p.theme.spacing(1)}px ${p.theme.spacing(1.5)}px`,
  background: p.selected
    ? p.theme.custom.colors.blue.w100
    : p.theme.custom.colors.background.light,
  borderBottom: p.theme.custom.border.standard,
  boxShadow: p.dragging ? '0 0 8px 1px rgba(0,0,0,.15)' : 'none',
  minHeight: p.theme.spacing(5),

  cursor: p.disabled ? 'inherit' : 'grab',

  '> *': {
    opacity: p.disabled ? 0.3 : 1
  },

  gap: p.theme.spacing(1)
}));

const FieldContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%'
}));

const FieldLabel = styled('div')((p) => ({
  flex: '1'
}));

const FieldIcons = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center'
}));

const FieldIcon = styled(ButtonBase)((p) => ({
  '&&': {
    fontSize: p.theme.custom.fontSize.s,
    paddingLeft: p.theme.spacing(0.5),
    paddingRight: p.theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',

    gap: p.theme.spacing(0.5)
  }
}));

const RequiredFieldSearchContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(1)
}));

const RadioItemsContainer = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  gap: p.theme.spacing(1)
}));

const RadioItemContainer = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: p.theme.spacing(0.5)
}));

enum AutoEnroll {
  APPROVAL_NEEDED = 'APPROVAL_NEEDED',
  APPROVAL_NOT_NEEDED = 'APPROVAL_NOT_NEEDED'
}

type EnrollmentDialogTabName = 'settings' | 'submissions';
const TAB_NAMES: EnrollmentDialogTabName[] = ['settings', 'submissions'];

const DROP_IDS = {
  fieldsSelectorContent: 'droppable-filed-selector-content',
  formFieldOrder: 'droppable-form-field-order'
};

export type DadoEnrollmentFieldOption = {
  type: 'text' | 'email' | ' date' | 'address' | 'userRelation';
  label: string;
  key: string;
};

export const useEnrollmentDialogTabQueryParam = (param: string = 'tab') => {
  return useQueryParam<EnrollmentDialogTabName>(
    param,
    (v) =>
      v && TAB_NAMES.includes(v as EnrollmentDialogTabName)
        ? (v as EnrollmentDialogTabName)
        : 'settings',
    (v) => (v ? v : undefined)
  );
};

const EnrollmentDialogTabs: React.FC<{
  value: EnrollmentDialogTabName;
  onChange: (v: EnrollmentDialogTabName) => void;
  tpl: Doc<IExperienceTemplate>;
}> = ({ onChange, value, tpl }) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const root = css({
    '&&': {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      '&:first-of-type': {
        paddingLeft: theme.spacing(0)
      }
    }
  });
  return (
    <Tabs
      value={value}
      onChange={(ev, newValue: EnrollmentDialogTabName) => onChange(newValue)}
      indicatorColor="primary"
      textColor="primary"
      style={{ borderWidth: 0, borderColor: 'white' }}
    >
      <Tab
        value="settings"
        label={t('Settings')}
        classes={{ root }}
        onClick={() => {}}
      />
      <Tab
        value="submissions"
        label={
          <FlexContainer>
            <div>{t('Submissions')}</div>
            <EnrollmentFormSubmissionsPendingBubble tpl={tpl} />
          </FlexContainer>
        }
        classes={{ root }}
        onClick={() => {}}
      />
    </Tabs>
  );
};

export const EnrollmentFormDialogStatus = ({
  enrollmentForm,
  isTemplateActive,
  getFormMeasurements
}: {
  enrollmentForm: IEnrollmentForm;
  isTemplateActive: boolean;
  getFormMeasurements?: () => DOMRect | null;
}) => {
  const { t } = useUnsafeTranslation();
  const enrollmentFormUrl = toStandaloneAppEnrollmentFormUrl(enrollmentForm);
  const isReallyActive =
    enrollmentForm.accessTokensInfo[enrollmentForm.accessTokens[0]]?.scopes
      .length > 0;

  const { enqueueSnackbarSuccess } = useSnackbar();

  return (
    <FormStatus status={enrollmentForm.status}>
      <FlexContainer justifyContent="space-between">
        <FlexContainer>
          {enrollmentForm.status === EnrollmentFormStatus.ACTIVE ? (
            <Power size={18} />
          ) : (
            <PowerOff size={18} />
          )}
          {enrollmentForm.status !== EnrollmentFormStatus.ACTIVE && (
            <LightMdToJsx component="span">
              {t(
                isTemplateActive
                  ? 'This form is inactive. You must launch this experience before you can activate the form'
                  : !isReallyActive
                  ? `This form is **inactive**. It can't be accessed or submitted. Previous submissions can still be approved or rejected.`
                  : 'This form will be **deactivated** after saving.'
              )}
            </LightMdToJsx>
          )}
          {enrollmentForm.status === EnrollmentFormStatus.ACTIVE && (
            <>
              {isReallyActive ? (
                <div>
                  <LightMdToJsx component="span">
                    {t(
                      `This form is **active**. It can be accessed and submitted at`
                    )}
                  </LightMdToJsx>{' '}
                  <LinkExternal href={enrollmentFormUrl} color="primary" />
                </div>
              ) : (
                <LightMdToJsx component="span">
                  {t(`This form will be **activated** after saving.`)}
                </LightMdToJsx>
              )}
            </>
          )}
        </FlexContainer>
        {isReallyActive && (
          <Button
            variant="text"
            color="primary"
            startIcon={<Code size={16} />}
            onClick={() => {
              const formRect = getFormMeasurements?.() || null;
              const height = Math.max(
                roundNumberToNext(formRect?.height || 0) + 60, // + 60 is just random additional space
                500
              );
              navigator.clipboard.writeText(
                createEmbeddableIframeForUrl(enrollmentFormUrl, {
                  width: '100%',
                  height: `${height}px`
                })
              );
              enqueueSnackbarSuccess(t('Embed code copied to your clipboard!'));
            }}
          >
            {t('Copy embed code')}
          </Button>
        )}
      </FlexContainer>
    </FormStatus>
  );
};

const NoResults = ({ textSearch }: { textSearch: string }) => {
  const { t } = useUnsafeTranslation();

  return (
    <FlexContainer direction="column" alignItems={'start'}>
      <Typography variant="body1">
        <LightMdToJsx>
          {t('Field **{{ textSearch }}** not found.', { textSearch })}
        </LightMdToJsx>
      </Typography>
    </FlexContainer>
  );
};

export const RequiredFieldSearch = ({
  userFields,
  onChange
}: {
  userFields: DadoFieldOption[];
  onChange: (next: DadoFieldOption) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const [searchText, setSearchText] = useState('');
  const [selected, setSelected] = useState(null);

  return (
    <RequiredFieldSearchContainer>
      <Autocomplete
        options={userFields}
        value={selected}
        fullWidth
        filterSelectedOptions
        filterOptions={(options) => {
          if (searchText) {
            const search = toSearchTermRegexp(searchText);
            return options.filter((o) => search.test(o.label));
          }
          return options;
        }}
        onChange={(e, v) => {
          if (v) {
            onChange(v);
            setSearchText('');
          }
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'reset') {
            setSelected(null);
            return;
          }
          setSearchText(newInputValue);
        }}
        noOptionsText={<NoResults textSearch={searchText} />}
        getOptionLabel={(option) => {
          return option.label || 'unknown';
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                value: searchText
              }}
              label={t('Add a data field')}
            />
          );
        }}
      />
    </RequiredFieldSearchContainer>
  );
};

export const RequiredFieldItem = ({
  field,
  selected,
  dragging,
  onSelectField,
  onRemoveField
}: {
  field: EnrollmentFormField;
  selected: boolean;
  dragging: boolean;
  onSelectField: (nextField: string) => void;
  onRemoveField: (toDelete: string) => void;
}) => {
  const dragRef = useRef<HTMLDivElement>(null);
  const hovered = useHover(dragRef);
  const isFieldActive = hovered || selected;
  const userFieldLableMap = useUserFieldLableMap();
  const getFieldLabel = (field: EnrollmentFormField): string => {
    const userFieldTarget = getUserFieldTarget(field);
    return userFieldTarget
      ? userFieldLableMap[userFieldTarget.userField]
      : field.renderParameters.displayName;
  };

  return (
    <>
      <FieldsListItem
        ref={dragRef}
        onClick={() => onSelectField(field.id)}
        dragging={dragging}
        disabled={false}
        selected={selected}
      >
        <FieldContainer>
          <FieldLabel>{getFieldLabel(field)}</FieldLabel>
          {isFieldActive && (
            <FieldIcons>
              <FieldIcon>
                <GripVertical size={16} />
              </FieldIcon>
              {!field.mandatory && (
                <FieldIcon onClick={() => onRemoveField(field.id)}>
                  <Trash2 size={16} />
                </FieldIcon>
              )}
            </FieldIcons>
          )}
        </FieldContainer>
      </FieldsListItem>
    </>
  );
};

const HIDDEN_USER_FIELD_KEYS: (keyof IUser)[] = ['active', 'alternateId'];

const TextFieldParameters = ({
  formField,
  onChange
}: {
  formField: EnrollmentTextField;
  onChange: (changedField: EnrollmentFormField) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const userFieldLableMap = useUserFieldLableMap();
  const userFieldTarget = getUserFieldTarget(formField);
  const getFieldLabel = (field: EnrollmentFormField): string => {
    return userFieldLableMap[userFieldTarget?.userField || ''];
  };

  const onRenderParamsChanged = (
    param: keyof EnrollmentTextField['renderParameters'],
    value: any
  ) => {
    const newParams: EnrollmentTextField['renderParameters'] = {
      ...formField.renderParameters
    };
    newParams[param] = value; //not reall typesafe
    const newField: EnrollmentTextField = {
      ...formField,
      renderParameters: newParams
    };
    onChange(newField);
  };

  const onValidationParamsChanged = (
    param: keyof EnrollmentTextField['validationParameters'],
    value: any
  ) => {
    const newParams: EnrollmentTextField['validationParameters'] = {
      ...formField.validationParameters,
      [param]: value
    };

    const newField: EnrollmentTextField = {
      ...formField,
      validationParameters: newParams
    };
    onChange(newField);
  };

  const isNameField: Boolean =
    formField.id === DefaultDadoField.FIRST_NAME ||
    formField.id === DefaultDadoField.LAST_NAME ||
    formField.id === DefaultDadoField.PREFERRED_NAME ||
    formField.id === DefaultDadoField.MIDDLE_NAME;

  return (
    <>
      <FormRow>
        <TextField
          label="Display name"
          value={formField.renderParameters.displayName}
          onChange={(ev) =>
            onRenderParamsChanged('displayName', ev.target.value)
          }
          fullWidth
          placeholder={t('Display name')}
          variant="outlined"
        />
      </FormRow>

      {!isNameField && (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={formField.validationParameters.restrictAnswerChoice}
                onChange={(ev) =>
                  onValidationParamsChanged(
                    'restrictAnswerChoice',
                    ev.target.checked
                  )
                }
                color="primary"
              />
            }
            label={
              <FlexContainer>
                <Typography variant="body2">
                  {t('Restrict answer choice')}
                </Typography>
              </FlexContainer>
            }
          />

          {formField.validationParameters.restrictAnswerChoice && (
            <RadioPanel
              value={formField.validationParameters.restrictionType}
              orientation="vertical"
              onChange={(value) =>
                onValidationParamsChanged('restrictionType', value)
              }
              options={compact([
                userFieldTarget &&
                  FIELDS_WHICH_CAN_RESTRICT_TO_EXISTING_VALUES.includes(
                    userFieldTarget.userField as any
                  ) && {
                    label: t('List existing field values'),
                    value: TextRestriction.EXISTING,
                    description: lightMdToJsx(
                      t(
                        `Existing values for "${getFieldLabel(
                          formField
                        )}" in your database will be shown as options to pick from.`
                      )
                    )
                  },
                {
                  label: t('Enter a list of allowed values'),
                  value: TextRestriction.LIST,
                  description: lightMdToJsx(
                    t(`Manually enter options user can select`)
                  ),
                  body: formField.validationParameters.restrictionType ===
                    TextRestriction.LIST && (
                    <>
                      <OptionBuilder
                        value={
                          formField.validationParameters.allowedValuesList || []
                        }
                        onChange={(nextOptions) =>
                          onValidationParamsChanged(
                            'allowedValuesList',
                            nextOptions
                          )
                        }
                        onLabelChange={(oldLabel, newLabel, i) => {
                          const currentOptions =
                            formField.validationParameters.allowedValuesList ||
                            [];
                          const nextOptions = [...currentOptions];
                          nextOptions[i] = newLabel;
                          onValidationParamsChanged(
                            'allowedValuesList',
                            nextOptions
                          );
                        }}
                        textToNewOption={(s) => s}
                        // Using the index here is not a good idea, it still creates
                        // focus problems when dragging (after the drop, the wrong item will be focused)
                        // This is better than the alternative of not being able to type properly when we use s
                        // Ideally these options would be more than just plain strings so that they could be
                        // identified properly
                        optionToKey={(s, i) => `${i}`}
                        optionToLabel={(s) => s}
                        label={t('Add a new option')}
                      />
                    </>
                  )
                }
              ])}
            />
          )}
        </>
      )}
    </>
  );
};

const EmailFieldParameters = ({
  formField,
  onChange
}: {
  formField: EmailField;
  onChange: (changedField: EnrollmentFormField) => void;
}) => {
  const { t } = useUnsafeTranslation();

  const onRenderParamsChanged = (
    param: keyof EmailField['renderParameters'],
    value: any
  ) => {
    const newParams: EmailField['renderParameters'] = {
      ...formField.renderParameters
    };
    newParams[param] = value; //not reall typesafe
    const newField: EmailField = {
      ...formField,
      renderParameters: newParams
    };
    onChange(newField);
  };
  return (
    <>
      <FormRow>
        <TextField
          label="Display name"
          value={formField.renderParameters.displayName}
          onChange={(ev) =>
            onRenderParamsChanged('displayName', ev.target.value)
          }
          fullWidth
          placeholder={t('Display name')}
          variant="outlined"
        />
      </FormRow>
    </>
  );
};

const UserRelationshipFieldParameters = ({
  formField,
  onChange
}: {
  formField: UserRelationField;
  onChange: (changedField: UserRelationField) => void;
}) => {
  const { t } = useUnsafeTranslation();

  const onRenderParamsChanged = (
    param: keyof UserRelationField['renderParameters'],
    value: any
  ) => {
    const newParams: UserRelationField['renderParameters'] = {
      ...formField.renderParameters
    };
    newParams[param] = value; //not reall typesafe
    const newField: UserRelationField = {
      ...formField,
      renderParameters: newParams
    };
    onChange(newField);
  };
  return (
    <>
      <FormRow>
        <TextField
          label="Display name"
          value={formField.renderParameters.displayName}
          onChange={(ev) =>
            onRenderParamsChanged('displayName', ev.target.value)
          }
          fullWidth
          placeholder={t('Display name')}
          variant="outlined"
        />
      </FormRow>
    </>
  );
};

const NumberFieldParameters = ({
  formField,
  onChange
}: {
  formField: NumberField;
  onChange: (changedField: EnrollmentFormField) => void;
}) => {
  const { t } = useUnsafeTranslation();

  const onValidationParamsChange = (
    param: keyof NumberField['validationParameters'],
    value: any
  ) => {
    const newParams: NumberField['validationParameters'] = {
      ...formField.validationParameters,
      [param]: value
    };

    const newField: NumberField = {
      ...formField,
      validationParameters: newParams
    };
    onChange(newField);
  };

  const onRenderParamsChanged = (
    param: keyof NumberField['renderParameters'],
    value: any
  ) => {
    const newParams: NumberField['renderParameters'] = {
      ...formField.renderParameters
    };
    newParams[param] = value; //not reall typesafe
    const newField: NumberField = {
      ...formField,
      renderParameters: newParams
    };
    onChange(newField);
  };

  return (
    <>
      <FormRow>
        <TextField
          label="Display name"
          value={formField.renderParameters.displayName}
          onChange={(ev) =>
            onRenderParamsChanged('displayName', ev.target.value)
          }
          fullWidth
          placeholder={t('Display name')}
          variant="outlined"
        />
      </FormRow>
      <FormControlLabel
        control={
          <Switch
            checked={formField.validationParameters.restrictToInterval}
            onChange={(ev) =>
              onValidationParamsChange('restrictToInterval', ev.target.checked)
            }
            color="primary"
          />
        }
        label={
          <FlexContainer>
            <Typography variant="body2">
              {t('Restrict answer range')}
            </Typography>
          </FlexContainer>
        }
      />

      {formField.validationParameters.restrictToInterval && (
        <>
          <FormRow>
            <TextField
              label="Minimum value"
              inputProps={{ type: 'number' }}
              value={formField.validationParameters.min}
              onChange={(ev) => {
                const newValue = Number.parseInt(ev.target.value);
                onValidationParamsChange('min', newValue);
              }}
              fullWidth
            />

            <TextField
              label="Maximum value"
              inputProps={{ type: 'number' }}
              value={formField.validationParameters.max}
              onChange={(ev) => {
                const newValue = Number.parseInt(ev.target.value);
                onValidationParamsChange('max', newValue);
              }}
              fullWidth
            />
          </FormRow>
        </>
      )}
    </>
  );
};

const AdressFieldParameters = ({
  formField,
  onChange
}: {
  formField: AddressField;
  onChange: (changedField: EnrollmentFormField) => void;
}) => {
  const { t } = useUnsafeTranslation();

  const onRenderParamsChanged = (
    param: keyof AddressField['renderParameters'],
    value: any
  ) => {
    const newParams: AddressField['renderParameters'] = {
      ...formField.renderParameters
    };
    newParams[param] = value; //not reall typesafe
    const newField: AddressField = {
      ...formField,
      renderParameters: newParams
    };
    onChange(newField);
  };

  const onValidationParamsChanged = (
    param: keyof AddressField['validationParameters'],
    value: any
  ) => {
    const newParams: AddressField['validationParameters'] = {
      ...formField.validationParameters,
      [param]: value
    };

    const newField: AddressField = {
      ...formField,
      validationParameters: newParams
    };
    onChange(newField);
  };
  return (
    <>
      <FormRow>
        <TextField
          label="Display name"
          value={formField.renderParameters.displayName}
          onChange={(ev) =>
            onRenderParamsChanged('displayName', ev.target.value)
          }
          fullWidth
          placeholder={t('Display name')}
          variant="outlined"
        />
      </FormRow>

      <FormControlLabel
        control={
          <Switch
            checked={!formField.validationParameters.nameRequired}
            onChange={(ev) =>
              onValidationParamsChanged('nameRequired', !ev.target.checked)
            }
            color="primary"
          />
        }
        label={
          <FlexContainer>
            <Typography variant="body2">{t("Don't require name")}</Typography>
          </FlexContainer>
        }
      />
    </>
  );
};

const DateFieldParameters = ({
  formField,
  onChange,
  dateFormat
}: {
  formField: DateField;
  onChange: (changedField: EnrollmentFormField) => void;
  dateFormat: string;
}) => {
  const { t } = useUnsafeTranslation();

  const onValidationParamsChange = (
    param: keyof DateFieldValidationParameters,
    value: any
  ) => {
    const newParams: DateFieldValidationParameters = {
      ...formField.validationParameters
    };
    newParams[param] = value; //not reall typesafe
    const newField: DateField = {
      ...formField,
      validationParameters: newParams
    };
    onChange(newField);
  };

  const onRenderParamsChanged = (
    param: keyof DateField['renderParameters'],
    value: any
  ) => {
    const newParams: DateField['renderParameters'] = {
      ...formField.renderParameters
    };
    newParams[param] = value; //not reall typesafe
    const newField: DateField = {
      ...formField,
      renderParameters: newParams
    };
    onChange(newField);
  };

  return (
    <>
      <FormRow>
        <TextField
          label="Display name"
          value={formField.renderParameters.displayName}
          onChange={(ev) =>
            onRenderParamsChanged('displayName', ev.target.value)
          }
          fullWidth
          placeholder={t('Display name')}
          variant="outlined"
        />
      </FormRow>
      <FormControlLabel
        control={
          <Switch
            checked={formField.validationParameters.restrictToInterval}
            onChange={(ev) =>
              onValidationParamsChange('restrictToInterval', ev.target.checked)
            }
            color="primary"
          />
        }
        label={
          <FlexContainer>
            <Typography variant="body2">
              {t('Restrict answer period')}
            </Typography>
          </FlexContainer>
        }
      />

      {formField.validationParameters.restrictToInterval && (
        <>
          <FormRow>
            <KeyboardDatePicker
              label="From"
              format={dateFormat}
              value={
                formField.validationParameters.from &&
                toMoment(formField.validationParameters.from)
              }
              inputVariant="outlined"
              variant="inline"
              onChange={(ev) => {
                if (ev) {
                  const m = moment(ev.toDate()).startOf('d');
                  const newValue: Timestamp = fromMoment(m);
                  onValidationParamsChange('from', newValue);
                }
              }}
              fullWidth
            />

            <KeyboardDatePicker
              label="To"
              value={
                formField.validationParameters.to &&
                toMoment(formField.validationParameters.to)
              }
              format={dateFormat}
              inputVariant="outlined"
              variant="inline"
              onChange={(ev) => {
                if (ev) {
                  const m = moment(ev.toDate()).endOf('d');
                  const newValue: Timestamp = fromMoment(m);
                  onValidationParamsChange('to', newValue);
                }
              }}
              fullWidth
            />
          </FormRow>
        </>
      )}
    </>
  );
};

const FieldParameters = ({
  formField,
  onChange,
  dateFormat
}: {
  formField: EnrollmentFormField;
  onChange: (changedField: EnrollmentFormField) => void;
  dateFormat: string;
}): JSX.Element => {
  switch (formField.type) {
    case FieldType.DATE:
      return (
        <DateFieldParameters
          formField={formField}
          onChange={onChange}
          dateFormat={dateFormat}
        />
      );
    case FieldType.TEXT:
      return <TextFieldParameters formField={formField} onChange={onChange} />;
    case FieldType.EMAIL:
      return <EmailFieldParameters formField={formField} onChange={onChange} />;
    case FieldType.ADDRESS:
      return (
        <AdressFieldParameters formField={formField} onChange={onChange} />
      );
    case FieldType.USER_RELATIONSHIP:
      return (
        <UserRelationshipFieldParameters
          formField={formField}
          onChange={onChange}
        />
      );
    case FieldType.NUMBER:
      return (
        <NumberFieldParameters formField={formField} onChange={onChange} />
      );
  }
};

type RequiredEmailFields =
  | DefaultDadoField.PERSONAL_EMAIL
  | DefaultDadoField.EMAIL
  | 'both';

const EnrollmentFormBuilder = ({
  enrollmentForm,
  additionalCompositeFields = [],
  currentUser,
  onChange,
  isTemplateActive
}: {
  enrollmentForm: IEnrollmentForm;
  additionalCompositeFields?: CompositeUserFieldKey[];
  currentUser: ICurrentUser;
  isTemplateActive: boolean;
  onChange: React.Dispatch<React.SetStateAction<IEnrollmentForm | undefined>>;
}) => {
  const { t } = useUnsafeTranslation();
  const org = currentUser.org;
  const { customUserFields: customFields } = org;
  const userFields = useUserFields(customFields);

  const userFieldsWithComposites: DadoFieldOption[] = useMemo(
    () =>
      [...compositeUserFields(additionalCompositeFields, t), ...userFields]
        .filter((f) => !HIDDEN_USER_FIELD_KEYS.includes(f.key as any))
        .filter((f) => !enrollmentForm.fields.map(getFieldKey).includes(f.key))
        // Email addresses are added through a separate section and don't need to be selected here
        .filter((f) => f.key !== 'personalEmail' && f.key !== 'email'),
    [userFields, additionalCompositeFields, t, enrollmentForm.fields]
  );

  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(
    enrollmentForm.fields[0]?.id || null
  );
  const selectedField = useMemo(
    () => enrollmentForm.fields.find((field) => field.id === selectedFieldId),
    [enrollmentForm.fields, selectedFieldId]
  );
  const selectFieldId = useCallback(
    (fieldId: string) => {
      if (fieldId === null && selectedField) {
      }
      setSelectedFieldId(fieldId);
    },
    [selectedField]
  );

  const onRemoveField = (fieldId: string) => {
    const fields = filter(
      enrollmentForm.fields,
      (field) => field.id !== fieldId
    );
    const nextValue: IEnrollmentForm = { ...enrollmentForm, fields: fields };
    onChange(nextValue);
  };

  type UserField = CustomUserField | StandardUserField;
  const getEmptyFormFields = (
    userField: UserField
  ): EnrollmentFormField | undefined => {
    // this is a bit of a type hack, if you give this field a  type like Partial<EnrollmentFormField> it breaks
    const fieldBase = {
      id: userField.key,
      mandatory:
        userField.key === DefaultDadoField.EMAIL ||
        userField.key === DefaultDadoField.PERSONAL_EMAIL,
      targets: [
        {
          id: shortid(),
          type: FieldDataDestinationType.USER_PROFILE,
          userField: userField.key
        }
      ],
      renderParameters: {
        displayName: userField.label
      }
    };
    const type: UserField['type'] | 'email' =
      userField.key === DefaultDadoField.EMAIL ||
      userField.key === DefaultDadoField.PERSONAL_EMAIL
        ? 'email'
        : userField.type;

    switch (type) {
      case 'string':
        return {
          type: FieldType.TEXT,
          validationParameters: {
            restrictAnswerChoice: false,
            allowedValuesList: null,
            restrictionType: null
          },
          ...fieldBase
        };
      case 'address':
        return {
          ...fieldBase,
          type: FieldType.ADDRESS,
          validationParameters: {
            nameRequired: true
          }
        };
      case 'date':
        return {
          ...fieldBase,
          type: FieldType.DATE,
          validationParameters: {
            restrictToInterval: false,
            from: null,
            to: null
          }
        };
      case 'email':
        return {
          ...fieldBase,
          type: FieldType.EMAIL,
          validationParameters: {}
        };
      case 'relationship':
        return {
          ...fieldBase,
          type: FieldType.USER_RELATIONSHIP,
          renderParameters: {
            displayName: `${userField.label} (enter work email address)`
          },
          validationParameters: {}
        };
      case 'number':
        return {
          ...fieldBase,
          type: FieldType.NUMBER,
          validationParameters: {
            restrictToInterval: false,
            min: null,
            max: null
          }
        };
    }
  };

  const onAddFormField = (field: DadoFieldOption) => {
    const userField = userFields.find((uf) => uf.key === field.key);

    if (userField) {
      const enrollmentFormField = getEmptyFormFields(userField);

      const exists = !!enrollmentForm.fields.find((f) => {
        const userFieldTarget = getUserFieldTarget(f);
        return userFieldTarget?.userField === userField.key;
      });

      if (enrollmentFormField && !exists) {
        const nextValue: IEnrollmentForm = {
          ...enrollmentForm,
          fields: [...enrollmentForm.fields, enrollmentFormField]
        };
        onChange(nextValue);
      } else {
        console.error(' User field type not supported.');
      }
    }
  };

  const onIntroductionChange = (nextValue: string) => {
    onChange((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        introduction: { t: nextValue, v: 1 }
      };
    });
  };

  const onChangeFieldOrder = (sortedFieldIds: string[]) => {
    const sortedFields = sortBy(enrollmentForm.fields, (field) =>
      sortedFieldIds.indexOf(field.id)
    );
    onChange({ ...enrollmentForm, fields: sortedFields });
  };

  const enableForm = (enrollmentForm: IEnrollmentForm, val: boolean) => {
    const newValue: IEnrollmentForm = {
      ...enrollmentForm,
      status: val ? EnrollmentFormStatus.ACTIVE : EnrollmentFormStatus.INACTIVE
    };
    onChange(newValue);
  };

  const _onRequiredEmailChange = (value: string) => {
    const requiredEmailFields =
      value === 'both'
        ? [DefaultDadoField.PERSONAL_EMAIL, DefaultDadoField.EMAIL]
        : [value as DefaultDadoField];

    const nextFields = enrollmentForm.fields.filter((f) => {
      const fieldKey = getFieldKey(f);
      return (
        (fieldKey !== DefaultDadoField.PERSONAL_EMAIL &&
          fieldKey !== DefaultDadoField.EMAIL) ||
        requiredEmailFields.includes(fieldKey)
      );
    });
    const missingFields = compact(
      requiredEmailFields.map((emailField) => {
        const userField = userFields.find((f) => f.key === emailField);
        if (
          userField &&
          !nextFields.find((f) => getFieldKey(f) === emailField)
        ) {
          return getEmptyFormFields(userField);
        }
        return null;
      })
    );
    return {
      ...enrollmentForm,
      fields: [...missingFields, ...nextFields]
    };
  };

  const onRequiredEmailChange = (value: string) => {
    onChange(_onRequiredEmailChange(value));
  };

  const onAutoEnrollChange = (val: string) => {
    const newValue: IEnrollmentForm = {
      ...enrollmentForm,
      autoEnroll: {
        ...enrollmentForm.autoEnroll,
        active: val === AutoEnroll.APPROVAL_NOT_NEEDED
      }
    };
    onChange(newValue);
  };

  const onFieldChange = (changedField: EnrollmentFormField) => {
    const newFields = enrollmentForm.fields.map((f) => {
      if (f.id === changedField.id) {
        return changedField;
      }
      return f;
    });
    const newFormValue: IEnrollmentForm = {
      ...enrollmentForm,
      fields: newFields
    };
    onChange(newFormValue);
  };

  const presentEmailFields = compact(
    enrollmentForm.fields
      .filter((f) => {
        const userField = getUserFieldTarget(f)?.userField;
        return (
          userField === DefaultDadoField.PERSONAL_EMAIL ||
          userField === DefaultDadoField.EMAIL
        );
      })
      .map((f) => getUserFieldTarget(f)?.userField)
  );

  const requiredEmailValue: RequiredEmailFields | null =
    presentEmailFields.includes(DefaultDadoField.PERSONAL_EMAIL) &&
    presentEmailFields.includes(DefaultDadoField.EMAIL)
      ? 'both'
      : presentEmailFields.includes(DefaultDadoField.PERSONAL_EMAIL)
      ? DefaultDadoField.PERSONAL_EMAIL
      : presentEmailFields.includes(DefaultDadoField.EMAIL)
      ? DefaultDadoField.EMAIL
      : null;

  const requireAdminApproval: AutoEnroll = enrollmentForm.autoEnroll.active
    ? AutoEnroll.APPROVAL_NOT_NEEDED
    : AutoEnroll.APPROVAL_NEEDED;

  return (
    <EnrollmentFormBuilderContainer>
      <FormContainer>
        <FormHeader>
          <FormControlLabel
            control={
              <Switch
                checked={enrollmentForm.status === 'ACTIVE' ? true : false}
                onChange={(ev) => enableForm(enrollmentForm, ev.target.checked)}
                color="primary"
                disabled={isTemplateActive}
              />
            }
            label={<Typography variant="h5">{t('Activate form')}</Typography>}
          />
        </FormHeader>
        <FormSection>
          <FlexContainer
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
            spacing={1}
            fullWidth
          >
            <Typography variant="h5" component="h5">
              {t('Form submissions')}
            </Typography>
            <RadioGroup
              value={requireAdminApproval}
              onChange={(ev, value) => onAutoEnrollChange(value)}
              aria-labelledby="required-email-radio-buttons-group-label"
              name="required-email"
            >
              <RadioItemsContainer>
                <RadioItemContainer>
                  <FormControlLabel
                    value={AutoEnroll.APPROVAL_NEEDED}
                    control={<Radio color="primary" />}
                    label={t('Require admin approval')}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {t(
                      'A Dado admin must approve submissions before enrollment occurs.'
                    )}
                  </Typography>
                </RadioItemContainer>
                <RadioItemContainer>
                  <FormControlLabel
                    value={AutoEnroll.APPROVAL_NOT_NEEDED}
                    control={<Radio color="primary" />}
                    label={t('Auto-enroll')}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {t(
                      `Form submissions are automatically enrolled in the experience, without any approval step.`
                    )}
                  </Typography>
                </RadioItemContainer>
              </RadioItemsContainer>
            </RadioGroup>
          </FlexContainer>
        </FormSection>
        <FormBody>
          <FlexContainer
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
            spacing={3}
            fullWidth
          >
            <FlexContainer
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              fullWidth
            >
              <Typography variant="h5" component="h5">
                {t('Introduction text')}
              </Typography>
              <Typography variant="body2" component="p" color="textSecondary">
                {t('Displayed at the top of the form')}
              </Typography>
              <MarkdownEditor
                value={enrollmentForm.introduction.t}
                rows={8}
                onChange={onIntroductionChange}
                fullWidth
              />
            </FlexContainer>

            <FlexContainer
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              fullWidth
            >
              <Typography variant="h5" component="h5">
                {t('Required email field')}
              </Typography>
              <RadioGroup
                value={requiredEmailValue}
                row
                onChange={(ev, value) => onRequiredEmailChange(value)}
                aria-labelledby="required-email-radio-buttons-group-label"
                name="required-email"
              >
                <FormControlLabel
                  value={DefaultDadoField.PERSONAL_EMAIL}
                  control={<Radio color="primary" />}
                  label={t('Personal')}
                />
                <FormControlLabel
                  value={DefaultDadoField.EMAIL}
                  control={<Radio color="primary" />}
                  label={t('Work')}
                />
                <FormControlLabel
                  value="both"
                  control={<Radio color="primary" />}
                  label={t('Both')}
                />
              </RadioGroup>
            </FlexContainer>
            <FlexContainer
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              fullWidth
            >
              <Typography variant="h5" component="h5">
                {t('Required fields')}
              </Typography>
              <Typography variant="body2" component="p" color="textSecondary">
                {t('Data to collect and enroll someone in this experience')}
              </Typography>
              <FlexContainer
                justifyContent="flex-start"
                alignItems="flex-start"
                fullWidth
                spacing={4}
                marginTop={1}
              >
                <FieldsContainer>
                  <RequiredFieldSearch
                    userFields={userFieldsWithComposites}
                    onChange={(value) => onAddFormField(value)}
                  />
                  <DragDropContext
                    onDragEnd={(r) => {
                      if (r.destination?.droppableId) {
                        if (r.source.droppableId === DROP_IDS.formFieldOrder) {
                          onChangeFieldOrder(
                            arrayMove(
                              enrollmentForm.fields.map((field) => field.id),
                              r.source.index,
                              r.destination.index
                            )
                          );
                        }
                      }
                    }}
                  >
                    <Droppable
                      droppableId={DROP_IDS.formFieldOrder}
                      isDropDisabled={false}
                    >
                      {(provided, snapshot) => (
                        <DragDropContainer
                          highlight={snapshot.isDraggingOver}
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {enrollmentForm.fields.map((field, i) => {
                            return (
                              <Draggable
                                key={field.id}
                                draggableId={field.id}
                                index={i}
                                isDragDisabled={false}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <RequiredFieldItem
                                      field={field}
                                      dragging={snapshot.isDragging}
                                      selected={selectedFieldId === field.id}
                                      onSelectField={selectFieldId}
                                      onRemoveField={onRemoveField}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                        </DragDropContainer>
                      )}
                    </Droppable>
                  </DragDropContext>
                </FieldsContainer>
                <ParametersContainer>
                  {selectedField ? (
                    <FieldParameters
                      formField={selectedField}
                      onChange={onFieldChange}
                      dateFormat={org.dateFormats.MMDDYYYY}
                    />
                  ) : (
                    <div>{t('Select a field to view details')}</div>
                  )}
                </ParametersContainer>
              </FlexContainer>
            </FlexContainer>
          </FlexContainer>
        </FormBody>
      </FormContainer>
    </EnrollmentFormBuilderContainer>
  );
};

const SettingsContainer = styled('div')((p) => ({
  background: p.theme.custom.colors.gray.w100,
  padding: p.theme.spacing(2),
  display: 'flex',
  gap: p.theme.spacing(2),
  height: 'calc(100% - 54px)' // 54 is the height of the status bar
}));

const validateEnrollmentFormData = (
  d: IEnrollmentForm
): {
  field: EnrollmentFormField;
  error: 'NO_RESTRICTED_VALUES_GIVEN'; // & other things later maybe
}[] => {
  return flatten(
    // so that multiple errors could be returned from one field
    compact(
      d.fields.map((f) => {
        if (f.type === FieldType.TEXT) {
          if (
            f.validationParameters.restrictAnswerChoice &&
            f.validationParameters.restrictionType === TextRestriction.LIST
          ) {
            if (!f.validationParameters.allowedValuesList?.length) {
              return {
                field: f,
                error: 'NO_RESTRICTED_VALUES_GIVEN'
              };
            }
          }
        }
        return null;
      })
    )
  );
};

const EnrollmentFormPreviw = forwardRef<
  {
    getFormMeasurements: () => DOMRect | null;
  },
  {
    enrollmentFormData: IEnrollmentForm;
    tpl: Doc<IExperienceTemplate>;
    org: IOrganization;
    onFormSubmit: (
      submissonData: FormFieldSubmission[]
    ) => Promise<SubmitFormResponse>;
  }
>(({ enrollmentFormData, tpl, org, onFormSubmit }, formRef) => {
  const [enrollmentFormDenormalized] = usePromise(() => {
    return toEnrollmentFormDenormalized(enrollmentFormData, tpl.data, org);
  }, [enrollmentFormData, tpl, org]);

  console.log('denorm in preview', enrollmentFormDenormalized);

  if (!enrollmentFormDenormalized) {
    return null;
  }

  return (
    <StandaloneAppContextProvider
      initialThemeOverrides={enrollmentFormDenormalized.org.experienceTheming}
    >
      <EnrollmentForm
        enrollmentForm={enrollmentFormDenormalized}
        onSubmit={onFormSubmit}
        preview
        ref={formRef}
      />
    </StandaloneAppContextProvider>
  );
});

export const EnrollmentFormDialog = ({
  tpl,
  open,
  onClose,
  tab,
  onChangeTab,
  isTemplateInactive: isTemplateActive
}: {
  tpl: Doc<IExperienceTemplate>;
  open: boolean;
  onClose: () => void;
  tab: EnrollmentDialogTabName;
  onChangeTab: (nextTab: EnrollmentDialogTabName) => void;
  isTemplateInactive: boolean;
}) => {
  const formRef = useRef<{
    getFormMeasurements: () => DOMRect | null;
  }>(null);
  const currentUser = useCurrentUser();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  const discardDialogState = useDialogState();

  const [enrollmentFormDoc] = useEnrollmentFormByTemplateOrGetEmptyDefault(
    tpl,
    currentUser.id
  );
  const [
    enrollmentFormData,
    setEnrollmentFormData
  ] = useState<IEnrollmentForm>();

  useEffect(() => {
    if (enrollmentFormDoc) {
      setEnrollmentFormData(enrollmentFormDoc.data);
    }
  }, [enrollmentFormDoc]);

  const [afterDiscard, setAfterDiscard] = useState<'close' | 'submission'>(
    'close'
  );

  const isDirty =
    enrollmentFormDoc && enrollmentFormData !== enrollmentFormDoc.data;

  const onConfirmDiscardChanges = () => {
    if (enrollmentFormDoc) {
      setEnrollmentFormData(enrollmentFormDoc.data);
    }

    switch (afterDiscard) {
      case 'close':
        onClose();
        break;
      case 'submission':
        onChangeTab('submissions');
        break;
    }

    discardDialogState.closeDialog();
  };

  const onCancelDiscardChanges = () => {
    discardDialogState.closeDialog();
  };

  const { t } = useUnsafeTranslation();

  const closeDialog = () => {
    if (isDirty) {
      setAfterDiscard('close');
      discardDialogState.openDialog();
    } else {
      onClose();
    }
  };

  if (!enrollmentFormData) {
    return null;
  }

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));
  const onFormSumbit = async (
    formFieldSubmissions: FormFieldSubmission[]
  ): Promise<SubmitFormResponse> => {
    const sumbissionDTO: EnrollmentFormSubmissionDTO = {
      accessToke: 'wololo',
      data: formFieldSubmissions
    };

    const firstNameField = enrollmentFormData.fields.find(
      (f) => getUserFieldTarget(f)?.userField === DefaultDadoField.FIRST_NAME
    );
    const lastNameField = enrollmentFormData.fields.find(
      (f) => getUserFieldTarget(f)?.userField === DefaultDadoField.LAST_NAME
    );
    const emailField = enrollmentFormData.fields.find(
      (f) => getUserFieldTarget(f)?.userField === DefaultDadoField.EMAIL
    );
    const personalEmailField = enrollmentFormData.fields.find(
      (f) =>
        getUserFieldTarget(f)?.userField === DefaultDadoField.PERSONAL_EMAIL
    );
    const firstname = sumbissionDTO.data.find(
      (d) => d.id === firstNameField?.id
    )?.value as string | undefined;
    const lastName = sumbissionDTO.data.find((d) => d.id === lastNameField?.id)
      ?.value as string | undefined;
    const email = sumbissionDTO.data.find((d) => d.id === emailField?.id)
      ?.value as string | undefined;

    const personalEmail = sumbissionDTO.data.find(
      (d) => d.id === personalEmailField?.id
    )?.value as string | undefined;

    await sleep(2000);
    return {
      status: SubmissionResponseStatus.SUCCESS,
      isTryOutDadoOrg: tpl.data.orgId === DADO_TRY_OUT_ORG_ID,
      enrolleeInfo: {
        fullName: `${firstname || ''}  ${lastName || ''}`,
        email: `${email || personalEmail || ''}`
      }
    };
  };

  const _onChangeTab = (nextTab: EnrollmentDialogTabName) => {
    if (isDirty) {
      setAfterDiscard('submission');
      discardDialogState.openDialog();
    } else {
      onChangeTab(nextTab);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={closeDialog} fullScreen>
        <DialogActionsHeader
          hasVerticalPadding={false}
          onClose={closeDialog}
          left={
            <EnrollmentDialogTabs
              onChange={_onChangeTab}
              value={tab}
              tpl={tpl}
            />
          }
        />

        <DialogContentWithoutPadding>
          <EnrollmentFormDialogStatus
            enrollmentForm={enrollmentFormData}
            isTemplateActive={isTemplateActive}
            getFormMeasurements={formRef.current?.getFormMeasurements}
          />
          {tab === 'settings' && (
            <SettingsContainer>
              <LeftSideContainer>
                <EnrollmentFormBuilder
                  currentUser={currentUser}
                  enrollmentForm={enrollmentFormData}
                  onChange={setEnrollmentFormData}
                  isTemplateActive={isTemplateActive}
                />
              </LeftSideContainer>
              <RightSideContainer>
                {enrollmentFormData && (
                  <EnrollmentFormPreviw
                    enrollmentFormData={enrollmentFormData}
                    onFormSubmit={onFormSumbit}
                    tpl={tpl}
                    org={currentUser.org}
                    ref={formRef}
                  />
                )}
              </RightSideContainer>
            </SettingsContainer>
          )}
          {tab === 'submissions' && (
            <>
              {enrollmentFormDoc && (
                <Submissions enrollmentFormDoc={enrollmentFormDoc} tpl={tpl} />
              )}
            </>
          )}
        </DialogContentWithoutPadding>

        {tab === 'settings' && (
          <DialogActionsWithSlots
            right={
              <>
                <Button color="primary" onClick={closeDialog}>
                  {t('Cancel')}
                </Button>
                {enrollmentFormDoc && tab === 'settings' && (
                  <ButtonWithPromise
                    color="primary"
                    variant="contained"
                    disabled={!isDirty}
                    pending={`${t('Saving')}...`}
                    onClick={async () => {
                      // make this a fine granular transactional update, so that there's never a weird situation with overwriting
                      // a task status
                      try {
                        if (!enrollmentFormData) {
                          return;
                        }

                        const errors = validateEnrollmentFormData(
                          enrollmentFormData
                        );
                        if (errors.length) {
                          enqueueSnackbarError(
                            <div>
                              <div>
                                {t(
                                  'Please correct the following problems in your form'
                                )}
                              </div>
                              {errors.map((e, i) => (
                                <React.Fragment key={i}>
                                  {e.error === 'NO_RESTRICTED_VALUES_GIVEN' && (
                                    <div>
                                      • {e.field.renderParameters.displayName}:{' '}
                                      {t('Select restricted answer choices')}
                                    </div>
                                  )}
                                </React.Fragment>
                              ))}
                            </div>
                          );
                          return;
                        }

                        const newDoc = {
                          ...enrollmentFormDoc,
                          data: enrollmentFormData
                        };
                        await setDoc(newDoc);

                        enqueueSnackbarSuccess(t('Changes saved!'));
                      } catch (err: any) {
                        console.warn(err);
                        enqueueSnackbarError(
                          t(
                            'Failed to apply changes. Please try again or contact support.'
                          )
                        );
                      }
                    }}
                  >
                    {t('Save changes')}
                  </ButtonWithPromise>
                )}
              </>
            }
          />
        )}
      </Dialog>
      <DiscardChangesDialog
        open={discardDialogState.dialogOpen}
        onCancel={onCancelDiscardChanges}
        onConfirm={onConfirmDiscardChanges}
      />
    </>
  );
};
